import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import './index.css'
import App from './App.jsx'
import { ErrorBoundary } from './components/error-boundary.jsx'
import { initSentry } from './lib/sentry.js'

// Initialize crash reporting BEFORE anything else renders, so we catch
// errors during app boot. No-op unless VITE_SENTRY_DSN is configured.
initSentry()

// Force light theme app-wide. StoryPlayer scopes itself back to dark
// via data-theme="dark" on its own root. Any stale localStorage value
// from older builds is intentionally ignored.
document.documentElement.setAttribute('data-theme', 'light')
try { localStorage.removeItem('narrative-theme') } catch {}

// Mark web-only platform so the desktop mobile-frame CSS only engages
// in a real browser (Capacitor native iOS/Android stays unmarked, so
// nothing affects the production mobile apps — including iPad which
// would otherwise match the desktop media-query width threshold).
if (!Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('platform-web')
}

// iOS native setup
if (Capacitor.isNativePlatform()) {
  import('@capacitor/status-bar').then(({ StatusBar, Style }) => {
    StatusBar.setOverlaysWebView({ overlay: true })
    // Style.Light = light background, dark text (counter-intuitive name).
    // We want dark text on our white app bg.
    StatusBar.setStyle({ style: Style.Light })
  }).catch(() => {})

  import('@capacitor/keyboard').then(({ Keyboard }) => {
    Keyboard.setAccessoryBarVisible({ isVisible: false })
  }).catch(() => {})

  // The Capacitor SplashScreen plugin is configured with
  // launchShowDuration: 0 + launchAutoHide: true so it dismisses
  // itself immediately. The native iOS LaunchScreen.storyboard is
  // the only splash the user sees, handled entirely by iOS.
  //
  // Body bg is set to --inv-bg (#FFFFFF light) so that transient
  // gaps between navigation commits don't flash an unrelated color.
  // (Previously this was forced to #000000 for the dark theme — kept
  // imperative so it stays in sync with the runtime light theme even
  // if a parent page accidentally clears its bg.)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.body.style.backgroundColor = '#FFFFFF'
    })
  })
}

// Cross-route theater dim. A persistent black overlay outside the
// React tree that survives navigation commits.
//
// Uses the Web Animations API (el.animate) instead of CSS transitions.
// Reliability pattern: before each animation, explicitly set inline
// opacity to the START value. This way, canceling any in-flight
// animation reverts cleanly to a known state. On finish, commit the
// END value to inline style so subsequent reads see the correct value.
const DIM_DURATION = 650
const DIM_EASING = 'cubic-bezier(0.4, 0, 0.6, 1)'
let dimAnim = null

function animateDim(el, fromOpacity, toOpacity) {
  if (dimAnim) { try { dimAnim.cancel() } catch {} }
  // Sync inline to the start value so the cancel+restart sequence is
  // visually continuous. Without this, the post-cancel revert can
  // briefly flash the wrong opacity.
  el.style.opacity = String(fromOpacity)
  const anim = el.animate(
    [{ opacity: fromOpacity }, { opacity: toOpacity }],
    { duration: DIM_DURATION, easing: DIM_EASING }
  )
  // On finish, commit the end state to inline style so future
  // reads (and future animations) see a clean baseline.
  anim.addEventListener('finish', () => {
    el.style.opacity = String(toOpacity)
  })
  anim.addEventListener('cancel', () => {
    // On cancel, the element reverts to whatever inline is — which is
    // the "from" value we just set. That's fine; the new animation
    // will overwrite it.
  })
  dimAnim = anim
  return anim
}

window.__theaterDim = {
  show() {
    const el = document.getElementById('theater-dim')
    if (!el) return
    el.style.pointerEvents = 'auto'
    animateDim(el, 0, 1)
  },
  hide() {
    const el = document.getElementById('theater-dim')
    if (!el) return
    el.style.pointerEvents = 'none'
    animateDim(el, 1, 0)
  },
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>,
)
